import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Model, RepairService } from '../types/BookingTypes';
import { 
  addToCart as addToCartAPI, 
  removeFromCart as removeFromCartAPI, 
  AddToCartRequest,
  RemoveFromCartRequest,
  getSessionId
} from '../services/cartService';
import { getCurrentUser, isLoggedIn } from '../services/authService';

export interface CartItem {
  id?: number; // Add ID for API items
  model: Model;
  services: (RepairService & { cartItemId?: number })[];
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (model: Model, services: RepairService[]) => void;
  removeFromCart: (modelId: number, serviceId: number) => void;
  updateServicesInCart: (modelId: number, services: RepairService[]) => Promise<void>;
  clearCart: () => void;
  refreshCart: () => Promise<void>;
  isLoading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load cart from localStorage
  const refreshCart = useCallback(async () => {
    try {
      const savedCart = localStorage.getItem('minto_cart');
      const cartData = savedCart ? JSON.parse(savedCart) : [];
      setCart(cartData);
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      setCart([]);
    }
  }, []); // No dependencies needed

  // Save cart to localStorage
  const saveCartToLocalStorage = (cartData: CartItem[]) => {
    try {
      localStorage.setItem('minto_cart', JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  // Load cart on component mount
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = (model: Model, services: RepairService[]) => {
    if (!isLoggedIn()) {
      console.error('User not authenticated');
      return;
    }

    // OPTIMISTIC UPDATE: Immediately update localStorage and UI
    // Use negative numbers for temporary IDs to distinguish from real ones
    const optimisticServices = services.map((service, index) => ({
      ...service,
      cartItemId: -(Date.now() + index) // Negative temporary ID
    }));

    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(item => item.model.id === model.id);
      
      let updatedCart: CartItem[];
      if (existingItemIndex !== -1) {
        // Update existing item
        updatedCart = [...prevCart];
        const existingServices = new Set(updatedCart[existingItemIndex].services.map(s => s.id));
        const newServices = optimisticServices.filter(s => !existingServices.has(s.id));
        
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          services: [...updatedCart[existingItemIndex].services, ...newServices]
        };
      } else {
        // Add new item
        updatedCart = [...prevCart, { model, services: optimisticServices }];
      }
      
      // Save to localStorage immediately for instant UI update
      saveCartToLocalStorage(updatedCart);
      return updatedCart;
    });

    // BACKGROUND API CALLS: Fire and forget - no await, no blocking
    (async () => {
      try {
        const user = getCurrentUser();
        if (!user) throw new Error('User not found');
        const sessionId = getSessionId();

        const servicesWithCartIds: (RepairService & { cartItemId: number })[] = [];
        
        for (const service of services) {
          const addRequest: AddToCartRequest = {
            sessionId,
            customerMobile: user.phoneNumber,
            makeId: model.brandId,
            makeName: model.brand || '',
            modelId: model.id,
            modelName: model.name,
            serviceId: service.id,
            serviceName: service.serviceName,
            serviceType: 'Repair',
            actualPrice: service.actualPrice,
            serviceCost: service.serviceCost,
            quantity: 1,
            warrantyTypeId: service.warrantyTypeId,
            warrantyName: service.warrantyName,
            warrantyPeriod: service.warrantyPeriod
          };

          try {
            const cartItemId = await addToCartAPI(addRequest);
            servicesWithCartIds.push({ ...service, cartItemId });
          } catch (error) {
            console.error(`Failed to add service ${service.id} to cart:`, error);
            // Continue with other services, but track failures
          }
        }

        // Update cart with real cart item IDs (replace temporary negative IDs)
        if (servicesWithCartIds.length > 0) {
          setCart(prevCart => {
            const updatedCart = prevCart.map(cartItem => {
              if (cartItem.model.id === model.id) {
                                  const updatedServices = cartItem.services.map(service => {
                    const realService = servicesWithCartIds.find(s => s.id === service.id);
                    return realService || service; // Keep temp ID if API failed
                  });
                return { ...cartItem, services: updatedServices };
              }
              return cartItem;
            });
            
            // Save updated cart with real IDs
            saveCartToLocalStorage(updatedCart);
            return updatedCart;
          });
        }

        // If some API calls failed, show warning but don't revert UI
        if (servicesWithCartIds.length < services.length) {
          console.warn('Some services failed to sync with server, but are saved locally');
        }

      } catch (error) {
        // If there's a major error, revert the optimistic update
        console.error('Error adding to cart:', error);
        
        // Revert cart to previous state
        setCart(prevCart => {
          const revertedCart = prevCart.map(cartItem => {
            if (cartItem.model.id === model.id) {
              // Remove services that were optimistically added
              const filteredServices = cartItem.services.filter(service => 
                !services.some(s => s.id === service.id)
              );
              return { ...cartItem, services: filteredServices };
            }
            return cartItem;
          }).filter(cartItem => cartItem.services.length > 0); // Remove empty cart items
          
          saveCartToLocalStorage(revertedCart);
          return revertedCart;
        });
      }
    })();
  };

  const removeFromCart = (modelId: number, serviceId: number) => {
    // Find the cart item and service to remove
    let cartItemIdToRemove: number | undefined;
    let serviceToRemove: RepairService & { cartItemId?: number } | undefined;

    const cartItem = cart.find(item => item.model.id === modelId);
    if (cartItem) {
      serviceToRemove = cartItem.services.find(service => service.id === serviceId);
      if (serviceToRemove && serviceToRemove.cartItemId) {
        cartItemIdToRemove = serviceToRemove.cartItemId;
      }
    }

    // OPTIMISTIC UPDATE: Immediately remove from UI and localStorage
    setCart(prevCart => {
      const updatedCart = prevCart.map(cartItem => {
        if (cartItem.model.id === modelId) {
          const filteredServices = cartItem.services.filter(service => service.id !== serviceId);
          return { ...cartItem, services: filteredServices };
        }
        return cartItem;
      }).filter(cartItem => cartItem.services.length > 0); // Remove empty cart items

      // Save to localStorage immediately for instant UI update
      saveCartToLocalStorage(updatedCart);
      return updatedCart;
    });

    // BACKGROUND API CALL: Fire and forget - no await, no blocking
    if (cartItemIdToRemove && cartItemIdToRemove > 0) {
      (async () => {
        try {
          await removeFromCartAPI({ cartItemId: cartItemIdToRemove! });
        } catch (error) {
          console.error('Failed to remove item from server cart:', error);
          
          // Revert the optimistic update if API call failed
          if (serviceToRemove) {
            setCart(prevCart => {
              const revertedCart = prevCart.map(cartItem => {
                if (cartItem.model.id === modelId) {
                  // Add the service back
                  const serviceExists = cartItem.services.some(s => s.id === serviceId);
                  if (!serviceExists) {
                    return {
                      ...cartItem,
                      services: [...cartItem.services, serviceToRemove!]
                    };
                  }
                }
                return cartItem;
              });
              
              // If no cart item exists for this model, recreate it
              const modelExists = revertedCart.some(item => item.model.id === modelId);
              if (!modelExists && cartItem) {
                revertedCart.push({
                  model: cartItem.model,
                  services: [serviceToRemove!]
                });
              }
              
              saveCartToLocalStorage(revertedCart);
              return revertedCart;
            });
          }
        }
      })();
          }
  };

  const updateServicesInCart = async (modelId: number, services: RepairService[]) => {
    if (!isLoggedIn()) {
      throw new Error('User must be logged in');
    }

    try {
      setIsLoading(true);
      
      // Get current services for this model to remove them from API first
      const currentModelItem = cart.find(item => item.model.id === modelId);
      if (currentModelItem) {
        // Remove existing services from API
        for (const service of currentModelItem.services) {
          if (service.cartItemId) {
            try {
              await removeFromCartAPI({ cartItemId: service.cartItemId });
            } catch (error) {
              console.error('Error removing service from API:', error);
            }
          }
        }
      }
      
      // If there are new services to add, add them via API
      const servicesWithCartIds: (RepairService & { cartItemId?: number })[] = [];
      if (services.length > 0) {
        const model = cart.find(item => item.model.id === modelId)?.model;
        if (model) {
          const user = getCurrentUser();
          if (!user) throw new Error('User not found');

          const sessionId = getSessionId();

          // Add each service to the API and collect cart item IDs
          for (const service of services) {
            const addRequest: AddToCartRequest = {
              sessionId,
              customerMobile: user.phoneNumber,
              makeId: model.brandId,
              makeName: model.brand || '',
              modelId: model.id,
              modelName: model.name,
              serviceId: service.id,
              serviceName: service.serviceName,
              serviceType: 'Repair',
              actualPrice: service.actualPrice,
              serviceCost: service.serviceCost,
              quantity: 1,
              warrantyTypeId: service.warrantyTypeId,
              warrantyName: service.warrantyName,
              warrantyPeriod: service.warrantyPeriod
            };

            try {
              const cartItemId = await addToCartAPI(addRequest);
              servicesWithCartIds.push({ ...service, cartItemId });
            } catch (error) {
              console.error('Error adding service to API:', error);
              servicesWithCartIds.push(service);
            }
          }
        }
      }
      
      // Update localStorage cart with new services and cart item IDs
      setCart(prevCart => {
        const updatedCart = prevCart.map(item => {
          if (item.model.id === modelId) {
            return { ...item, services: servicesWithCartIds };
          }
          return item;
        }).filter(item => item.services.length > 0);

        // Save to localStorage
        saveCartToLocalStorage(updatedCart);
        return updatedCart;
      });
    } catch (error) {
      console.error('Error updating services in cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('minto_cart');
  };

  return (
    <CartContext.Provider value={{ 
      cart, 
      addToCart, 
      removeFromCart, 
      updateServicesInCart, 
      clearCart, 
      refreshCart,
      isLoading 
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext; 